import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "motion/react";
import {
  AlertCircle,
  Download,
  File,
  FileArchive,
  FileAudio,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../lib/cn";
import { Button } from "../../primitives/Button";
import { Spinner } from "../../primitives/Spinner";
import { useReducedMotion } from "../../hooks/useReducedMotion";

/**
 * ArtifactBlock - downloadable file card. Renderer for the K2 `artifact`
 * block (`docs/contracts/block-registry.md`): the platform's chat
 * `BlockRenderer` passes an `artifact` block's `props` straight through, so
 * the prop names match the block payload exactly.
 *
 * A job (a run, an agent step) produces a file - a generated report, an
 * export, a rendered video - and streams it into the conversation as an
 * `artifact` block. The card shows the file, and the Download action is live
 * at every status so the user can pull a partial / in-progress file the
 * moment a `download_url` exists, not only once the job finishes.
 *
 * Block props (`{ artifact_id, name, mime, size_bytes, download_url, status, run_id?, message_id? }`):
 *   - `artifact_id` - stable id for the artifact. Required.
 *   - `name` - the file name shown on the card (e.g. `"q3-report.pdf"`).
 *     Required. Drives the fallback type icon when `mime` is absent.
 *   - `mime` - MIME type (e.g. `"application/pdf"`). Picks the type icon.
 *     Falls back to the extension in `name`, then a generic file glyph.
 *   - `size_bytes` - file size in bytes. Rendered human-readable
 *     (`1.2 MB`). Omit / `0` while a streaming job has not sized the file yet.
 *   - `download_url` - the href the Download action points at. Works at ANY
 *     status; absent only when the job has not yet produced a URL.
 *   - `status` - `"processing" | "ready" | "error"`:
 *       - `processing` - a subtle spinner + "Preparing..." label; download
 *         stays enabled (download-while-processing).
 *       - `ready` - clean, no status chrome.
 *       - `error` - a handled, destructive-tinted error state; download is
 *         still offered when a `download_url` is present (a partial file may
 *         still be useful).
 *   - `run_id?` / `message_id?` - provenance, carried through for the host to
 *     correlate the artifact back to its run / message. Not rendered.
 *
 * Client-only props (never part of the streamed block): `className`,
 * `errorMessage`, `downloadLabel`, `onDownload`, `animate`.
 *
 * Motion: on a status change the status row plays the house two-beat
 * choreography - it slides into place first (spring on `y`), then the opacity
 * ramps after a short delay. `prefers-reduced-motion` collapses both beats.
 *
 * Theming is entirely token-driven (`bg-card`, `text-foreground`,
 * `text-muted-foreground`, `text-destructive`, ...) so per-studio retint +
 * dark mode work with no hardcoded colors. SSR-safe: no `window` / `document`
 * access at module top or render (the Download is a plain anchor; the optional
 * `onDownload` fires only in the click handler).
 */

export type ArtifactStatus = "processing" | "ready" | "error";

const artifactBlockVariants = cva(
  cn(
    "group/artifact flex items-center gap-3 rounded-2xl border bg-card p-4 text-card-foreground",
    "min-w-0 transition-colors duration-200 ease-smooth-out",
  ),
  {
    variants: {
      status: {
        processing: "border-border/70",
        ready: "border-border/70",
        error: "border-destructive/30 bg-destructive/5",
      },
    },
    defaultVariants: { status: "ready" },
  },
);

export interface ArtifactBlockProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "onError">,
    VariantProps<typeof artifactBlockVariants> {
  /** Stable artifact id. Required. */
  artifact_id: string;
  /** File name shown on the card. Required. */
  name: string;
  /** MIME type. Picks the type icon; falls back to the name's extension. */
  mime?: string;
  /** File size in bytes. Rendered human-readable. Omit while unsized. */
  size_bytes?: number;
  /** Download href. Works at any status; absent until the job emits one. */
  download_url?: string;
  /** Lifecycle status. Default `"ready"`. */
  status?: ArtifactStatus;
  /** Provenance: the run that produced the artifact. Not rendered. */
  run_id?: string;
  /** Provenance: the message the artifact belongs to. Not rendered. */
  message_id?: string;
  /** Copy shown in the `error` state. Defaults to a generic message. */
  errorMessage?: string;
  /** Label on the Download action. Defaults to `"Download"`. */
  downloadLabel?: string;
  /**
   * Fired when the Download action is activated, alongside the native
   * navigation (it does not preventDefault). Use for analytics, not to gate
   * the download.
   */
  onDownload?: (artifact: { artifact_id: string; name: string; status: ArtifactStatus }) => void;
  /** Animate the status row on status change. Default true; off under reduced motion. */
  animate?: boolean;
}

/* ============================================================================
 * MIME -> type icon
 *
 * A streamed `mime` may be missing or malformed, so the lookup is forgiving:
 * exact match, then the leading type group (`image/*`, `audio/*`, ...), then
 * the file extension on `name`, then a generic file glyph. Pure data - no
 * runtime cost beyond a couple of string ops.
 * ========================================================================= */

/** Exact MIME -> icon for the common document types worth a distinct glyph. */
const MIME_EXACT_ICON: Record<string, LucideIcon> = {
  "application/pdf": FileText,
  "application/json": FileCode,
  "application/zip": FileArchive,
  "application/x-zip-compressed": FileArchive,
  "application/gzip": FileArchive,
  "application/x-tar": FileArchive,
  "application/vnd.rar": FileArchive,
  "application/x-7z-compressed": FileArchive,
  "application/msword": FileText,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": FileText,
  "application/vnd.ms-excel": FileSpreadsheet,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": FileSpreadsheet,
};

/** Leading MIME group -> icon (`image/png` -> image glyph). */
const MIME_GROUP_ICON: Record<string, LucideIcon> = {
  image: FileImage,
  audio: FileAudio,
  video: FileVideo,
  text: FileText,
};

/** File extension -> icon, the fallback when `mime` is absent / unknown. */
const EXT_ICON: Record<string, LucideIcon> = {
  pdf: FileText,
  txt: FileText,
  md: FileText,
  csv: FileSpreadsheet,
  tsv: FileSpreadsheet,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  doc: FileText,
  docx: FileText,
  zip: FileArchive,
  tar: FileArchive,
  gz: FileArchive,
  rar: FileArchive,
  "7z": FileArchive,
  png: FileImage,
  jpg: FileImage,
  jpeg: FileImage,
  gif: FileImage,
  svg: FileImage,
  webp: FileImage,
  mp3: FileAudio,
  wav: FileAudio,
  ogg: FileAudio,
  mp4: FileVideo,
  mov: FileVideo,
  webm: FileVideo,
  json: FileCode,
  js: FileCode,
  ts: FileCode,
  tsx: FileCode,
  py: FileCode,
  html: FileCode,
  css: FileCode,
};

function iconForArtifact(mime: string | undefined, name: string): LucideIcon {
  if (mime) {
    const normalized = mime.trim().toLowerCase().split(";")[0];
    if (normalized in MIME_EXACT_ICON) return MIME_EXACT_ICON[normalized];
    const group = normalized.split("/")[0];
    if (group in MIME_GROUP_ICON) return MIME_GROUP_ICON[group];
  }
  const dot = name.lastIndexOf(".");
  if (dot > -1 && dot < name.length - 1) {
    const ext = name.slice(dot + 1).toLowerCase();
    if (ext in EXT_ICON) return EXT_ICON[ext];
  }
  return File;
}

/* ============================================================================
 * Human-readable size
 *
 * Deterministic (no locale-dependent grouping) so output is identical across
 * the SSR / hydration boundary. Binary units (1 KB = 1024 B), one decimal
 * place above bytes, trailing `.0` trimmed.
 * ========================================================================= */

const SIZE_UNITS = ["B", "KB", "MB", "GB", "TB", "PB"] as const;

function formatBytes(bytes: number | undefined): string | undefined {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return undefined;
  if (bytes < 1) return undefined;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const text = unit === 0 ? String(Math.round(value)) : value.toFixed(1).replace(/\.0$/, "");
  return `${text} ${SIZE_UNITS[unit]}`;
}

/* ============================================================================
 * ArtifactBlock
 * ========================================================================= */

export const ArtifactBlock = React.forwardRef<HTMLDivElement, ArtifactBlockProps>(
  function ArtifactBlock(
    {
      className,
      artifact_id,
      name,
      mime,
      size_bytes,
      download_url,
      status = "ready",
      run_id: _run_id,
      message_id: _message_id,
      errorMessage,
      downloadLabel = "Download",
      onDownload,
      animate = true,
      ...rest
    },
    ref,
  ) {
    const reducedMotion = useReducedMotion();
    const shouldAnimate = animate && !reducedMotion;

    const Icon = iconForArtifact(mime, name);
    const size = formatBytes(size_bytes);
    const isError = status === "error";
    const isProcessing = status === "processing";
    const canDownload = Boolean(download_url);

    const handleDownload = React.useCallback(() => {
      onDownload?.({ artifact_id, name, status });
    }, [onDownload, artifact_id, name, status]);

    return (
      <div
        ref={ref}
        className={cn(artifactBlockVariants({ status }), className)}
        data-slot="artifact"
        data-status={status}
        {...rest}
      >
        {/* File-type icon tile */}
        <span
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-lg border",
            isError
              ? "border-destructive/20 bg-destructive/10 text-destructive"
              : "border-border bg-muted/50 text-muted-foreground",
            "[&_svg]:size-5",
          )}
          aria-hidden
        >
          <Icon />
        </span>

        {/* Name + meta */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-sm font-medium text-foreground" title={name}>
            {name}
          </span>
          <StatusLine
            status={status}
            size={size}
            mime={mime}
            errorMessage={errorMessage}
            animate={shouldAnimate}
          />
        </div>

        {/* Download - live at any status whenever a url exists */}
        {canDownload ? (
          <Button
            asChild
            size="sm"
            variant={isError ? "outline" : "secondary"}
            className="shrink-0"
          >
            <a
              href={download_url}
              download={name}
              onClick={handleDownload}
              data-slot="artifact-download"
              aria-label={`${downloadLabel} ${name}`}
            >
              <Download aria-hidden />
              {downloadLabel}
            </a>
          </Button>
        ) : isProcessing ? (
          // No url yet: keep the affordance visible but inert until it arrives,
          // so the card does not reflow when the link streams in.
          <Button size="sm" variant="secondary" className="shrink-0" disabled>
            <Download aria-hidden />
            {downloadLabel}
          </Button>
        ) : null}
      </div>
    );
  },
);

ArtifactBlock.displayName = "ArtifactBlock";

/* ============================================================================
 * Internal - the status / meta line under the file name
 *
 * The meta line is one element per status so the house two-beat motion plays
 * on a real mount/unmount when the status changes: it slides up into place
 * first (spring on `y`), then the opacity ramps in after a short delay. The
 * `key` is the status, so React remounts the line on every transition.
 * ========================================================================= */

interface StatusLineProps {
  status: ArtifactStatus;
  size?: string;
  mime?: string;
  errorMessage?: string;
  animate: boolean;
}

function StatusLine({ status, size, mime, errorMessage, animate }: StatusLineProps) {
  // Slide-first-then-fade: `y` settles on a snappy spring; `opacity` is a
  // separate, slightly delayed tween so the move reads before the fade.
  const motionProps = animate
    ? {
        initial: { y: 4, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        transition: {
          y: { type: "spring" as const, stiffness: 600, damping: 28 },
          opacity: { duration: 0.18, delay: 0.06 },
        },
      }
    : {};

  if (status === "error") {
    return (
      <motion.span
        key="error"
        className="flex items-center gap-1 text-xs text-destructive"
        role="alert"
        {...motionProps}
      >
        <AlertCircle className="size-3 shrink-0" aria-hidden />
        <span className="truncate">{errorMessage ?? "Couldn't prepare this file."}</span>
      </motion.span>
    );
  }

  if (status === "processing") {
    return (
      <motion.span
        key="processing"
        className="flex items-center gap-1.5 text-xs text-muted-foreground"
        aria-live="polite"
        {...motionProps}
      >
        <Spinner size="sm" className="size-3" decorative />
        <span>Preparing...</span>
        {size ? <MetaDetail>{size}</MetaDetail> : null}
      </motion.span>
    );
  }

  // ready
  const typeLabel = shortTypeLabel(mime);
  return (
    <motion.span
      key="ready"
      className="flex items-center gap-1.5 text-xs text-muted-foreground"
      {...motionProps}
    >
      {size ? <span className="tabular-nums">{size}</span> : null}
      {size && typeLabel ? <MetaDot /> : null}
      {typeLabel ? <span className="uppercase">{typeLabel}</span> : null}
      {!size && !typeLabel ? <span>Ready</span> : null}
    </motion.span>
  );
}

function MetaDetail({ children }: { children: React.ReactNode }) {
  return (
    <>
      <MetaDot />
      <span className="tabular-nums">{children}</span>
    </>
  );
}

function MetaDot() {
  return <span className="inline-block size-0.5 rounded-full bg-border" aria-hidden />;
}

/**
 * A short, human-friendly type label from a MIME string for the `ready` meta
 * line: the subtype, upper-cased (`application/pdf` -> `PDF`), with the common
 * Office subtypes shortened. Returns `undefined` for an absent / unhelpful
 * mime so the line just shows the size.
 */
function shortTypeLabel(mime: string | undefined): string | undefined {
  if (!mime) return undefined;
  const normalized = mime.trim().toLowerCase().split(";")[0];
  const slash = normalized.indexOf("/");
  if (slash === -1) return undefined;
  let sub = normalized.slice(slash + 1);
  if (sub.includes("wordprocessingml")) return "DOCX";
  if (sub.includes("spreadsheetml")) return "XLSX";
  if (sub.includes("presentationml")) return "PPTX";
  // Strip vendor / x- prefixes that read as noise (`x-zip-compressed` -> `zip`).
  sub = sub.replace(/^x-/, "").replace(/^vnd\./, "");
  const plus = sub.indexOf("+");
  if (plus > -1) sub = sub.slice(0, plus);
  if (!sub || sub.length > 5) return undefined;
  return sub;
}

export { artifactBlockVariants, formatBytes, iconForArtifact };
export type ArtifactBlockVariantProps = VariantProps<typeof artifactBlockVariants>;
